import { useState, useEffect, useCallback } from 'react';
import { useBatch } from '@/contexts/BatchContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Link } from 'react-router-dom';
import { Plus, Search, Eye, Edit, Trash, MoreHorizontal, UserPlus } from 'lucide-react';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useToast } from '@/components/ui/use-toast';
import { InlineSpinner, CardLoadingSpinner } from '@/components/ui/loading-spinner';
import { Class } from '@/types';
import { classApi } from '@/lib/api';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const ClassesPage = () => {
  // Use batch context for all batch-related functionality
  const { currentBatch, hasBatches, loading: batchLoading } = useBatch();

  const [classes, setClasses] = useState<Class[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const { toast } = useToast();

  const fetchClasses = useCallback(async () => {
    // Don't fetch if batch context is still loading
    if (batchLoading) {
      console.log('Batch context is still loading, skipping classes fetch');
      return;
    }

    // Use current batch
    const batchId = currentBatch?.id;

    if (!batchId) {
      console.log('No batch available, cannot fetch classes');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      console.log('Fetching classes for batch:', batchId);
      const data = await classApi.getAll(batchId);
      console.log('Fetched classes:', data);
      // Ensure data is always an array
      setClasses(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching classes:', error);
      toast({
        title: 'Error',
        description: 'Failed to load classes. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [currentBatch?.id, batchLoading, toast]);

  useEffect(() => {
    fetchClasses();
  }, [fetchClasses]);

  const handleDeleteClick = (id: string) => {
    setDeleteId(id);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteId) return;

    try {
      setIsDeleting(true);
      await classApi.delete(deleteId);

      // Remove the deleted class from the state
      setClasses((Array.isArray(classes) ? classes : []).filter(c => c.id !== deleteId));

      toast({
        title: 'Success',
        description: 'Class deleted successfully',
      });
    } catch (error) {
      console.error('Error deleting class:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete class. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsDeleting(false);
      setDeleteId(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteId(null);
  };

  // Filter classes based on search term - ensure classes is always an array
  const filteredClasses = (Array.isArray(classes) ? classes : []).filter(c => {
    const searchLower = searchTerm.toLowerCase();
    return (
      c.name.toLowerCase().includes(searchLower) ||
      c.description.toLowerCase().includes(searchLower) ||
      c.grade.toLowerCase().includes(searchLower)
    );
  });

  // Show loading state while batch context is loading
  if (batchLoading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold tracking-tight">Classes</h1>
        </div>
        <Card>
          <CardContent>
            <CardLoadingSpinner message="Loading batch information..." />
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show error state when no batches are available
  if (!hasBatches) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold tracking-tight">Classes</h1>
        </div>
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-8">
            <p className="text-lg font-medium text-gray-900 mb-2">No Batches Available</p>
            <p className="text-gray-600 mb-4 text-center">
              You need to create a batch before you can manage classes.
              A batch represents a period for organizing classes.
            </p>
            <Button asChild>
              <Link to="/settings/batches">
                <Plus className="mr-2 h-4 w-4" />
                Create Batch
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold tracking-tight">Classes</h1>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle>Class Management</CardTitle>
          <Button
            onClick={() => {
              console.log('Create Class button clicked');
              window.location.href = '/classes/new';
            }}
          >
            <Plus className="mr-2 h-4 w-4" />
            Create Class
          </Button>
        </CardHeader>
        <CardContent>
          <div className="flex items-center mb-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                type="search"
                placeholder="Search classes..."
                className="pl-8"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          {loading ? (
            <CardLoadingSpinner message="Loading classes..." />
          ) : filteredClasses.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <h3 className="text-lg font-medium mb-2">No Classes Found</h3>
              <p className="text-muted-foreground text-center max-w-md">
                {(Array.isArray(classes) ? classes : []).length === 0
                  ? "You haven't created any classes for the current batch yet. Click the \"Create Class\" button to get started."
                  : "No classes match your search criteria. Try adjusting your search term."}
              </p>
            </div>
          ) : (
            <div className="border rounded-md">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Grade</TableHead>
                    <TableHead>Students</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredClasses.map((classItem) => (
                    <TableRow key={classItem.id}>
                      <TableCell className="font-medium">{classItem.name}</TableCell>
                      <TableCell>{classItem.grade}</TableCell>
                      <TableCell>{classItem.studentCount ?? classItem.studentIds?.length ?? 0} students</TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <span className="sr-only">Open menu</span>
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuLabel>Actions</DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem asChild>
                              <Link to={`/classes/${classItem.id}`}>
                                <Eye className="mr-2 h-4 w-4" />
                                View Details
                              </Link>
                            </DropdownMenuItem>
                            <DropdownMenuItem asChild>
                              <Link to={`/classes/${classItem.id}/edit`}>
                                <Edit className="mr-2 h-4 w-4" />
                                Edit Class
                              </Link>
                            </DropdownMenuItem>
                            <DropdownMenuItem asChild>
                              <Link to={`/classes/${classItem.id}/students`}>
                                <UserPlus className="mr-2 h-4 w-4" />
                                Manage Students
                              </Link>
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleDeleteClick(classItem.id)}
                              className="text-destructive focus:text-destructive"
                            >
                              <Trash className="mr-2 h-4 w-4" />
                              Delete Class
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={!!deleteId} onOpenChange={(open) => !open && handleDeleteCancel()}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the class
              and remove it from our servers.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? (
                <>
                  <InlineSpinner size="sm" className="mr-2" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ClassesPage;
